import { useCallback, useEffect, useState } from "react";
import { logger } from "core/utils/logger";
import { Grid,  Card, Box, Stack, Typography } from '@mui/material';
import { useSnackbar } from "notistack";
import { downloadTaxInvoices, getTaxReportData } from "services/reports";
import { format } from "date-fns";
import { fileSaver } from "core/utils/common";
import TaxReportFilters from "./TaxReportFilters";
import { lime, amber, lightBlue, brown } from '@mui/material/colors';
import { AccountBalanceRounded, AccountBalanceWalletRounded, CalculateRounded, CreditScoreRounded } from "@mui/icons-material";
import { formatCurrency } from "utils";

const TaxReportPage = ({pageRef}) => {
  logger.info('[TaxReportPage] mounting...');

  const [filters, setFilters] = useState(null);
  const [taxData, setTaxData] = useState([]);
  const {enqueueSnackbar} = useSnackbar();
  
  const loadData = useCallback((filters) => {
    setFilters(filters);
  }, []);

  const fetchReportData = useCallback((filters) => {
    logger.debug('[TaxReportPage]:{fetchReportData} fetching data');
    const controller = new AbortController();

    const data = {...filters, from: format(filters.from, 'dd-MM-yyyy'), to: format(filters.to, 'dd-MM-yyyy')};
    getTaxReportData(data, {signal: controller.signal, pageRef}).then(({response, error}) => {
      if(error?.message === 'canceled') return;
      // Return if any error
      if(error){
        setTaxData([]);
        return enqueueSnackbar(error?.message || 'Failed to fetch', {variant: 'error'});
      }
      setTaxData(response);
    });
    return controller;
  }, [pageRef, enqueueSnackbar]);
  
  useEffect(() => {
    let controller;
    if(filters){
      controller = fetchReportData(filters);
    }

    return () => controller?.abort();
  }, [filters, fetchReportData]);


  const downloadFile = useCallback(() => {
    logger.debug('[TaxReportPage]:{downloadFile} fetching file data');
    const data = {...filters, from: format(filters.from, 'dd-MM-yyyy'), to: format(filters.to, 'dd-MM-yyyy')};
    downloadTaxInvoices(data, {pageRef}).then(({response, error}) => {
      if(error?.message === 'canceled') return;
      // Return if any error
      if(error) enqueueSnackbar(error?.message, {variant: 'error'});

      const fileName = `Tax_Invoices_${new Date().toISOString().replace(/:/, '.')}.pdf`;
      fileSaver(response, fileName, 'application/pdf');
    });
  }, [filters, enqueueSnackbar, pageRef]);
  
  return (
    <Grid container p={2} spacing={1} >
      <TaxReportFilters loadData={loadData} downloadFile={downloadFile} />
      <Grid item container xs={12} spacing={2}  flexGrow={1} mt={1} justifyContent={{xs: 'center', md: 'flex-start'}}>
        <Grid item xs={12} sm={8} md={6} lg={4} xl={3}>
          <Card sx={{backgroundColor: lime[100], borderRadius: 4}} elevation={0}>
            <Box p={2}>
              <Stack direction="row" gap={2} alignItems="center">
                <CreditScoreRounded sx={{fontSize: '6rem', color: lime[500]}} />
                <Box>
                    <Typography fontWeight={800} variant='h5' color={lime[900]}>{taxData?.payments}</Typography>
                    <Typography variant='body2' >Total Payments</Typography>
                </Box>
              </Stack>
            </Box>
          </Card>
        </Grid>
        <Grid item xs={12} sm={8} md={6} lg={4} xl={3}>
          <Card sx={{backgroundColor: amber[100], borderRadius: 4}} elevation={0}>
            <Box p={2}>
              <Stack direction="row" gap={2} alignItems="center">
                <AccountBalanceRounded sx={{fontSize: '6rem', color: amber[500]}} />
                <Box>
                    <Typography fontWeight={800} variant='h5' color={amber[800]}>{formatCurrency(taxData?.grossCollected)}</Typography>
                    <Typography variant='body2'>Gross Collection</Typography>
                </Box>
              </Stack>
            </Box>
          </Card>
        </Grid>
        <Grid item xs={12} sm={8} md={6} lg={4} xl={3}>
          <Card sx={{backgroundColor: lightBlue[100], borderRadius: 4}} elevation={0}>
            <Box p={2}>
              <Stack direction="row" gap={1} alignItems="center">
                <AccountBalanceWalletRounded sx={{fontSize: '6rem', color: lightBlue[500]}} />
                <Box>
                    <Typography fontWeight={800} variant='h5' color={lightBlue[800]}>{formatCurrency(taxData?.netCollected)}</Typography>
                    <Typography variant='body2'>Net Collection</Typography>
                </Box>
              </Stack>
            </Box>
          </Card>
        </Grid>
        <Grid item xs={12} sm={8} md={6}  lg={4} xl={3}>
          <Card sx={{backgroundColor: brown[100], borderRadius: 4}} elevation={0}>
            <Box p={2}>
              <Stack direction="row" gap={1} alignItems="center">
                <CalculateRounded sx={{fontSize: '6rem', color: brown[500]}} />
                <Box>
                    <Typography fontWeight={800} variant='h5' color={brown[800]}>{formatCurrency(taxData?.taxToBePaid)}</Typography>
                    <Typography variant='body2'>Tax Collection</Typography>
                </Box>
              </Stack>
            </Box>
          </Card>
        </Grid>
      </Grid>
    </Grid>
  );
}
 
export default TaxReportPage;